import {
  AfterViewInit,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
  inject,
} from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { SearchOptions } from '../../search-options';
import { SearchDefinition } from '../../search-definition';
import { WebdokuService } from '../../../web-doku/webdoku.service';
import { NavigationMode } from '../../../shared/enums/navigation-mode';
import { ViewModelProperty } from '@ldw/roesberg';
import { StatusSelectComponent } from '../../../shared/components/status-select/status-select.component';
import { SettingsService } from '../../../shared/settings/settings.service';
import { LayoutService } from '../../../shared/services/layout.service';

@Component({
  selector: 'ldw-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.css'],
  standalone: false,
})
export class SearchComponent implements OnInit, AfterViewInit {
  private fb = inject(UntypedFormBuilder);
  private webDokuService = inject(WebdokuService);
  private settingsService = inject(SettingsService);
  private renderer = inject(Renderer2);
  private layoutService = inject(LayoutService);

  @Output() formClear: EventEmitter<SearchOptions> = new EventEmitter();
  @Output() formSubmit: EventEmitter<SearchOptions> = new EventEmitter();
  @Output() scanSelected: EventEmitter<SearchOptions> = new EventEmitter();
  @ViewChild('statusSelect') statusSelect!: StatusSelectComponent;

  _definition: SearchDefinition | undefined = undefined;
  form: UntypedFormGroup = new UntypedFormGroup({});
  searchControl!: AbstractControl;
  metaDataControl!: AbstractControl;
  wildCardControl!: AbstractControl;
  captionWidth = 230;
  propDoc = true;
  propLoops = false;
  propCustom = false;
  statusInitialValue = '';
  private searchFieldName = 'searchField';

  get isVerticalPropertyLayout(): boolean {
    return this.layoutService.isVerticalLayout();
  }

  get definition(): SearchDefinition | undefined {
    return this._definition;
  }

  @Input() set definition(value: SearchDefinition | undefined) {
    console.log('search component', value);
    if (this._definition == value) {
      this.setLastValues();
      return;
    }
    this._definition = value;
    this.setLastValues();
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      searchField: ['', [Validators.required]],
      useMetaDataField: [false, [Validators.required]],
      useWildCard: [false, [Validators.required]],
    });
    this.searchControl = this.form.controls[this.searchFieldName];
    this.metaDataControl = this.form.controls['useMetaDataField'];
    this.wildCardControl = this.form.controls['useWildCard'];
    if (this._definition != null) {
      this.statusInitialValue = this._definition?.docStatusSearchField?.value ?? '';
    }
  }

  ngAfterViewInit(): void {
    this.setLastValues();
    setTimeout(() => {
      const element = this.renderer.selectRootElement(`[ldwautofocus="true"]`);
      element.click();
    }, 0);
  }

  setLastValues() {
    if (this._definition == null) {
      return;
    }
    if (this.metaDataControl != null) {
      this.metaDataControl.patchValue(this._definition?.searchMetaData);
    }

    if (this.wildCardControl != null) {
      this.wildCardControl.patchValue(this._definition?.isWildCard);
    }

    this.propCustom = this._definition.navigationMode === NavigationMode.Custom;
    this.propLoops = this._definition.navigationMode === NavigationMode.LoopNodes;
    this.propDoc = this._definition.navigationMode === NavigationMode.Documents;
  }

  getStatusControl(): AbstractControl | undefined {
    return this.form.controls['statusField'];
  }

  getSearchTextDefinition(): ViewModelProperty[] | undefined {
    if (this._definition?.databaseSearchField == undefined) {
      return undefined;
    }
    const array: ViewModelProperty[] = [];
    array.push(this._definition.databaseSearchField);
    return array;
  }

  showDbSearchField(): boolean {
    if (this.metaDataControl.value == true) {
      return false;
    }
    return this._definition?.databaseSearchField != undefined;
  }

  get hasLoopNodes(): boolean {
    return this.webDokuService.currentWebDokuHasLoopNodes;
  }

  get hasCustomNavigation(): boolean {
    return this.webDokuService.currentWebDokuHasCustomNavigation;
  }

  get showLoopNodes(): boolean {
    return this.propLoops;
  }

  get showDocNodes(): boolean {
    return this.propDoc;
  }

  get showCustomNodes(): boolean {
    return this.propCustom;
  }
  setShowDocNodes() {
    this.propDoc = true;
    this.propLoops = false;
    this.propCustom = false;
  }

  setShowCustomNodes() {
    this.propCustom = true;
    this.propLoops = false;
    this.propDoc = false;
  }

  setShowLoopNodes() {
    this.propLoops = true;
    this.propCustom = false;
    this.propDoc = false;
  }

  scan() {
    const options = this.getSearchOptionsFromUI();
    this.scanSelected.emit(options);
  }

  clear() {
    if (this._definition == null) {
      return;
    }
    this._definition.clearInput();

    this._definition.searchMetaData = this.settingsService.settings.searchSettings.dbSearchAsDefaultEnabled;
    this._definition.isWildCard = this.settingsService.settings.searchSettings.isWildCard;
    this.statusInitialValue = this._definition.docStatusSearchField?.value ?? '';

    this.setLastValues();
    this.formClear.emit(this.getSearchOptionsFromUI());
    // not working for now, we have to force a rebind
    // const def = this._definition;
    // this.definition = undefined;
    // this.definition = def;
  }

  public submit() {
    const options = this.getSearchOptionsFromUI();
    if (options.searchValues.length > 0) {
      this.formSubmit.emit(options);
    }
  }

  private getSearchOptionsFromUI(): SearchOptions {
    const options = new SearchOptions();
    options.searchMetaData = this.metaDataControl.value;
    options.isWildCard = this.wildCardControl.value;

    options.addValueFromVMP(this.definition?.luceneSearchField);
    options.addValueFromControl(this.definition?.docStatusSearchField, this.getStatusControl());
    if (this.showDbSearchField()) {
      options.addValueFromVMP(this.definition?.databaseSearchField);
    }
    options.addValuesFromVMPList(this.definition?.docFields);
    if (this.showLoopNodes) {
      options.navigationMode = NavigationMode.LoopNodes;
      options.addValuesFromVMPList(this.definition?.loopFields);
      options.addValuesFromVMPList(this.definition?.lncFields);
      options.addValuesFromVMPList(this.definition?.lsnFields);
    } else if (this.showCustomNodes) {
      options.navigationMode = NavigationMode.Custom;
      options.addValuesFromVMPList(this.definition?.customFields);
    } else {
      options.navigationMode = NavigationMode.Documents;
      options.addValuesFromVMPList(this.definition?.nodeFields);
    }
    return options;
  }

  showToolTip(): boolean {
    return this.settingsService.settings.viewMode.showTooltips;
  }
}
